import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getItemsForSale, getSalePrice, purchaseEquipment, type EquipmentForSaleResponse, type EquipmentSalePriceResponse, type PurchaseEquipmentResponse } from '@/api/sales';

export const EQUIPMENT_SALES_KEYS = {
    all: ['equipment-sales'] as const,
    forSale: (params: {
        locationId?: string;
        equipmentTypeId?: string;
        condition?: string;
        page?: number;
        pageSize?: number;
    }) => [...EQUIPMENT_SALES_KEYS.all, 'for-sale', params] as const,
    price: (equipmentId: string) => [...EQUIPMENT_SALES_KEYS.all, 'price', equipmentId] as const,
};

/**
 * Hook to fetch equipment items available for sale
 */
export const useItemsForSale = (params: {
    locationId?: string;
    equipmentTypeId?: string;
    condition?: string;
    page?: number;
    pageSize?: number;
    enabled?: boolean;
}) => {
    const { enabled = true, ...queryParams } = params;

    return useQuery<EquipmentForSaleResponse>({
        queryKey: EQUIPMENT_SALES_KEYS.forSale(queryParams),
        queryFn: () => getItemsForSale(queryParams),
        enabled: enabled && !!queryParams.locationId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to fetch the sale price for a specific equipment item
 */
export const useSalePrice = (equipmentId: string, enabled: boolean = true) => {
    return useQuery<EquipmentSalePriceResponse>({
        queryKey: EQUIPMENT_SALES_KEYS.price(equipmentId),
        queryFn: () => getSalePrice(equipmentId),
        enabled: enabled && !!equipmentId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to purchase an equipment item
 */
export const usePurchaseEquipment = () => {
    const queryClient = useQueryClient();

    return useMutation<PurchaseEquipmentResponse, Error, string>({
        mutationFn: (equipmentId: string) => purchaseEquipment(equipmentId),
        onSuccess: () => {
            // Invalidate equipment sales queries after purchase
            queryClient.invalidateQueries({ queryKey: EQUIPMENT_SALES_KEYS.all });
        },
    });
};
